import {
  Alert,
  type AppShellLayout,
  type AppShellNavSection,
  Avatar,
  AvatarFallback,
  Button,
  AppShell as DesignSystemAppShell,
  DestructiveConfirmProvider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  FormLayoutDefaultsProvider,
} from '@avila-roffe/components-web';
import { useT as useTranslation } from '@avila-roffe/i18n';
import { ChatDrawer } from '@avila-roffe/settings';
import {
  Activity,
  BarChart2,
  BookOpen,
  Bot,
  ChevronsUpDown,
  ClipboardList,
  Database,
  LayoutDashboard,
  LogOut,
  Settings,
  UserRound,
} from 'lucide-react';
import React, { type ReactNode, Suspense, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { useAuth } from '../../auth/AuthContext';
import { useApplyAppearance } from '../../hooks/useApplyAppearance';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useFactoryApp } from '../../hooks/useFactoryApp';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useGatewaySocket } from '../../hooks/useGatewaySocket';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useQueueSync } from '../../hooks/useQueueSync';
import { useServiceWorkerUpdate } from '../../hooks/useServiceWorkerUpdate';
import { selectEffective, useAppearanceStore } from '../../stores/appearanceStore';
import { useAuthStore } from '../../stores/authStore';
import { PageHeaderProvider, usePageHeaderValue } from '../PageHeaderContext';
import { ActionPanel } from './ActionPanel';
import { ChatFab } from './ChatFab';
import { HealthDialog } from './HealthDialog';
import { NotificationPanel } from './NotificationPanel';

// ─── Router link adapter ──────────────────────────────────────────────────────

const RouterLink = React.forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement>
>(({ href, ...props }, ref) => <Link to={href ?? '#'} ref={ref} {...props} />);
RouterLink.displayName = 'RouterLink';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRole(role: string): string {
  return role
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ─── Logo mark ────────────────────────────────────────────────────────────────

interface LogoMarkProps {
  logoUrl?: string | null;
  name: string;
}

function LogoMark({ logoUrl, name }: LogoMarkProps) {
  const initials =
    name
      .split(/[\s-]/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || 'CF';

  return (
    <div className="flex items-center gap-2.5 px-1 py-0.5">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-bold select-none overflow-hidden">
        {logoUrl ? <img src={logoUrl} alt="" className="size-full object-cover" /> : initials}
      </div>
      <span className="text-sm font-semibold text-sidebar-foreground">{name}</span>
    </div>
  );
}

// ─── Sidebar user menu ────────────────────────────────────────────────────────

function SidebarUserMenu() {
  const { logout } = useAuth();
  const { t } = useTranslation('common');
  const currentUser = useCurrentUser();
  const navigate = useNavigate();

  const initials = currentUser?.initials ?? 'CF';
  const displayName = currentUser?.displayName ?? '…';
  const email = currentUser?.email;
  const primaryRole = currentUser?.roles[0] ? formatRole(currentUser.roles[0]) : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open user menu"
          className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-sidebar-accent/50"
        >
          <Avatar size="sm" shape="rounded">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-foreground">{displayName}</span>
            {primaryRole && (
              <span className="truncate text-xs text-muted-foreground">{primaryRole}</span>
            )}
          </div>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground/60" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" sideOffset={8} className="w-64">
        <div className="flex items-center gap-3 px-3 py-2">
          <Avatar size="sm" shape="rounded">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm font-semibold text-foreground leading-none">
              {displayName}
            </span>
            {email && email !== displayName && (
              <span className="truncate text-xs text-muted-foreground leading-none">{email}</span>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/preferences')}>
          <UserRound className="size-4" />
          {t('user_menu.my_account')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <Settings className="size-4" />
          {t('user_menu.settings')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/status')}>
          <Activity className="size-4" />
          {t('user_menu.system_status')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => void logout()}
          className="text-destructive focus:text-destructive focus:bg-destructive/10"
        >
          <LogOut className="size-4" />
          {t('user_menu.sign_out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Topbar actions ───────────────────────────────────────────────────────────

function TopbarActions() {
  return (
    <div className="flex items-center gap-1">
      <ActionPanel />
      <NotificationPanel />
    </div>
  );
}

// ─── AppShell ─────────────────────────────────────────────────────────────────

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  return (
    <PageHeaderProvider>
      <AppShellInner>{children}</AppShellInner>
    </PageHeaderProvider>
  );
}

function AppShellInner({ children }: AppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const pageHeader = usePageHeaderValue();
  const { t } = useTranslation('common');

  useApplyAppearance();
  const accessToken = useAuthStore((state) => state.accessToken);
  useQueueSync(accessToken);
  useGatewaySocket();

  const [healthOpen, setHealthOpen] = useState(false);

  const { pageActions: pageActionsEnabled, userMenu: userMenuEnabled } = useAppearanceStore(
    (s) => s.headerButtons,
  );
  const {
    mobileNavMode: _mobileNavMode,
    sidebarDefaultState,
    shellLayout: _shellLayout,
    confirmDestructive,
    formSpacing: _formSpacing,
    formActionsPosition: _formActionsPosition,
    formSectionStyle: _formSectionStyle,
  } = useAppearanceStore(useShallow(selectEffective));
  const shellLayout = (_shellLayout || 'app-shell-05') as AppShellLayout;
  const mobileNavMode = _mobileNavMode as 'overlay' | 'push' | 'underlay' | '';
  const formSpacing = _formSpacing as 'compact' | 'default' | 'relaxed' | 'spacious' | '';
  const formActionsPosition = _formActionsPosition as
    | 'bottom'
    | 'top'
    | 'both'
    | 'none'
    | 'sticky'
    | '';
  const formSectionStyle = _formSectionStyle as 'stacked' | 'side-label' | '';

  const { data: appData } = useFactoryApp();
  const appLogoUrl = appData?.logoUrl ?? null;
  const appName = appData?.displayName ?? appData?.name ?? 'Code Factory';

  const isOnline = useOnlineStatus();
  const { updateAvailable, applyUpdate } = useServiceWorkerUpdate();
  const _chatEnabled = useFeatureFlag('chat');

  const currentUser = useCurrentUser();

  const navItems = useMemo(
    () => [
      { href: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
      { href: '/agents', label: t('nav.agents'), icon: Bot },
      { href: '/work-items', label: t('nav.work_items'), icon: ClipboardList },
      { href: '/documentation', label: t('nav.documentation'), icon: BookOpen },
    ],
    [t],
  );

  const navGroups = useMemo(
    () => [
      {
        label: t('nav.factory'),
        icon: BarChart2,
        routes: [
          { href: '/factory/pipelines', label: t('nav.pipelines') },
          { href: '/factory/runs', label: t('nav.runs') },
          { href: '/factory/approvals', label: t('nav.approvals') },
          { href: '/factory/triggers', label: t('nav.triggers') },
          { href: '/factory/diagnostics', label: t('nav.diagnostics') },
        ],
      },
      {
        label: t('nav.agentic'),
        icon: Bot,
        routes: [
          { href: '/skills', label: t('nav.skills') },
          { href: '/tools', label: t('nav.tools') },
          { href: '/chat', label: t('nav.chat') },
          { href: '/playground', label: t('nav.playground') },
        ],
      },
      {
        label: t('nav.platform'),
        icon: Database,
        routes: [
          { href: '/workflows', label: t('nav.workflows') },
          { href: '/queues', label: t('nav.queues') },
          { href: '/workers', label: t('nav.workers') },
          { href: '/intake', label: t('nav.intake') },
          { href: '/pipeline', label: t('nav.ado_pipeline') },
          { href: '/status', label: t('nav.status') },
        ],
      },
    ],
    [t],
  );

  function toNavItem({
    href,
    label,
    icon,
  }: {
    href: string;
    label: string;
    icon?: React.ComponentType<{ className?: string }>;
  }) {
    return {
      icon,
      label,
      href,
      active: location.pathname === href || location.pathname.startsWith(`${href}/`),
    };
  }

  const nav: AppShellNavSection[] = [
    {
      items: [
        ...navItems.map(toNavItem),
        ...navGroups.map(({ label, icon, routes }) => ({
          icon,
          label,
          items: routes.map(({ href, label: subLabel }) => ({ label: subLabel, href })),
        })),
      ],
    },
  ];

  const mobileNav: AppShellNavSection[] = [
    { items: navItems.map(toNavItem) },
    ...navGroups.map(({ label, routes }) => ({
      label,
      items: routes.map(({ href, label: subLabel }) => ({
        label: subLabel,
        href,
        active: location.pathname === href || location.pathname.startsWith(`${href}/`),
      })),
    })),
  ];

  const routeTitleMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const { href, label } of navItems) map[href] = label;
    for (const { routes } of navGroups) for (const { href, label } of routes) map[href] = label;
    return map;
  }, [navItems, navGroups]);

  const derivedTitle = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    for (let i = parts.length; i > 0; i--) {
      const key = `/${parts.slice(0, i).join('/')}`;
      if (routeTitleMap[key]) return routeTitleMap[key];
    }
    return null;
  }, [location.pathname, routeTitleMap]);

  const resolvedTitle = pageHeader.title ?? derivedTitle ?? undefined;

  const mobileBackPath = pageHeader.mobileBackPath;
  const desktopBackPath = pageHeader.desktopBackPath;
  const backPath = pageHeader.backPath;

  const mobileOnBack =
    pageHeader.mobileOnBack ??
    (mobileBackPath ? () => navigate(mobileBackPath) : undefined) ??
    pageHeader.onBack ??
    (backPath ? () => navigate(backPath) : undefined);

  const desktopOnBack =
    pageHeader.desktopOnBack ??
    (desktopBackPath ? () => navigate(desktopBackPath) : undefined) ??
    pageHeader.onBack ??
    (backPath ? () => navigate(backPath) : undefined);

  const shellUser = currentUser
    ? {
        name: currentUser.displayName,
        subtitle: currentUser.roles[0] ? formatRole(currentUser.roles[0]) : '',
      }
    : undefined;

  const footer = (
    <footer className="mt-2">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 text-muted-foreground sm:px-6">
        <p className="w-full text-center text-sm sm:w-auto sm:text-left">
          © {new Date().getFullYear()} AvilaRoffe
        </p>
      </div>
    </footer>
  );

  const hideFooter =
    location.pathname.startsWith('/playground') ||
    location.pathname.startsWith('/chat') ||
    location.pathname.startsWith('/settings');

  return (
    <DestructiveConfirmProvider skipConfirm={confirmDestructive === false}>
      <FormLayoutDefaultsProvider
        spacing={formSpacing || undefined}
        actionsPosition={formActionsPosition || undefined}
        sectionVariant={formSectionStyle || undefined}
      >
        <Suspense>
          <DesignSystemAppShell
            layout={shellLayout}
            logo={<LogoMark logoUrl={appLogoUrl} name={appName} />}
            nav={nav}
            mobileNav={mobileNav}
            locationKey={location.key}
            sidebarFooter={<SidebarUserMenu />}
            user={userMenuEnabled ? shellUser : undefined}
            topbar={{
              showSearch: true,
              searchPlaceholder: 'Search agents, queues, work items…',
              mobileOnBack,
              desktopOnBack,
              actions: (
                <>
                  {pageActionsEnabled && pageHeader.action}
                  <TopbarActions />
                </>
              ),
              title: resolvedTitle,
              subtitle: pageHeader.subtitle,
              mobileAction: pageHeader.action,
            }}
            footer={hideFooter ? undefined : footer}
            noPaddingBottom={
              location.pathname.startsWith('/playground') || location.pathname.startsWith('/chat')
            }
            linkComponent={RouterLink}
            mobileOverlayMode={mobileNavMode || 'overlay'}
            sidebarDefaultCollapsed={sidebarDefaultState === 'collapsed'}
          >
            {updateAvailable && (
              <Alert
                variant="info"
                appearance="soft"
                className="rounded-none border-x-0 border-t-0 py-2"
                aria-live="polite"
                data-testid="update-banner"
              >
                <div className="flex items-center justify-between">
                  <span>A new version is available.</span>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="update-reload-btn"
                    onClick={applyUpdate}
                  >
                    Reload
                  </Button>
                </div>
              </Alert>
            )}
            {!isOnline && (
              <Alert
                variant="warning"
                appearance="soft"
                className="rounded-none border-x-0 border-t-0 py-2 text-center"
                aria-live="assertive"
              >
                You are offline. Some features may be unavailable.
              </Alert>
            )}
            <div
              className={
                location.pathname.startsWith('/documentation')
                  ? 'flex flex-col flex-1 min-h-0 overflow-hidden'
                  : [
                      'flex flex-col flex-1 overflow-y-auto gap-4 min-h-0',
                      /^\/chat\/(?!panel)/.test(location.pathname)
                        ? 'p-0 sm:p-6'
                        : location.pathname.startsWith('/settings')
                          ? 'py-4 sm:py-6'
                          : 'p-4 sm:p-6',
                    ].join(' ')
              }
            >
              {children}
            </div>
          </DesignSystemAppShell>

          <HealthDialog open={healthOpen} onOpenChange={setHealthOpen} />
          <ChatDrawer />
          <ChatFab />
        </Suspense>
      </FormLayoutDefaultsProvider>
    </DestructiveConfirmProvider>
  );
}

export default AppShell;
